import React, { useState, useEffect } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import {
  Container, Card, Form, Button, Table, Modal, Row, Col, Spinner, Badge,
} from "react-bootstrap";
import Swal from "sweetalert2";
import {
  getFirestore, collection, getDocs, addDoc, doc, updateDoc, deleteDoc, query, where,
} from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { GrEdit } from "react-icons/gr";
import { RiDeleteBin6Fill } from "react-icons/ri";
import { FaPlus, FaHistory, FaSearch, FaFileImport, FaDownload, FaTools, FaExclamationTriangle, FaBoxes, FaTags, FaDesktop, FaProjectDiagram } from "react-icons/fa";
import { BiBox, BiPlus, BiHistory, BiEdit, BiTrash, BiSearch, BiDownload, BiFile, BiDevices } from "react-icons/bi";
import * as XLSX from "xlsx";

const db = getFirestore(firebaseApp);

// Categorías de equipos eléctricos, medición, fuerza, cómputo, etc.
const CATEGORIAS_EQUIPOS = [
  "medicion",
  "electrico",
  "fuerza",
  "computo",
  "comunicaciones",
  "analizador",
  "calibrador",
  "fluke",
  "otro"
];

function CatalogoEquipos({ usuario }) {
  const ruc = usuario.ruc;
  const [equipos, setEquipos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [modoEdicion, setModoEdicion] = useState(false);
  const [equipoEditado, setEquipoEditado] = useState(null);

  const [formData, setFormData] = useState({
    nombre: "",
    descripcion: "",
    categoria: "",
    codigo: "",
    marca: "",
    modelo: "",
    serie: "",
    estado: "Disponible", // Estado por defecto
    trabajadorDni: "",
    trabajadorNombre: "",
    fechaPrestamo: null,
  });

  // Estadísticas
  const [stats, setStats] = useState({
    total: 0,
    disponibles: 0,
    prestados: 0,
    mantenimiento: 0
  });

  useEffect(() => {
    if (ruc) cargarEquipos();
  }, [ruc]);

  const cargarEquipos = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, "equipos_catalogo"), where("ruc_empresa", "==", ruc));
      const querySnapshot = await getDocs(q);
      const equiposData = [];
      let disp = 0, pres = 0, mant = 0;

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        equiposData.push({ id: doc.id, ...data });
        
        if (data.estado === "Disponible") disp++;
        else if (data.estado === "Prestado") pres++;
        else if (data.estado === "Mantenimiento" || data.estado === "Baja") mant++;
      });

      setEquipos(equiposData);
      setStats({
        total: equiposData.length,
        disponibles: disp,
        prestados: pres,
        mantenimiento: mant
      });
    } catch (error) {
      console.error("Error al cargar equipos:", error);
      Swal.fire({ title: "Error", text: "No se pudieron cargar los equipos", icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (!CATEGORIAS_EQUIPOS.includes(formData.categoria)) {
        Swal.fire({ title: "Error", text: "La categoría seleccionada no es válida.", icon: "error" });
        setLoading(false);
        return;
      }

      const dataToSave = {
        ...formData,
        ruc_empresa: ruc,
      };

      if (modoEdicion) {
        const docRef = doc(db, "equipos_catalogo", equipoEditado.id);
        await updateDoc(docRef, { ...dataToSave, fechaActualizacion: new Date() });
      } else {
        await addDoc(collection(db, "equipos_catalogo"), { ...dataToSave, fechaRegistro: new Date(), activo: true });
      }
      await cargarEquipos();
      limpiarFormulario();
      setShowModal(false);
      Swal.fire({ title: "¡Éxito!", text: modoEdicion ? "Equipo actualizado correctamente" : "Equipo agregado correctamente", icon: "success" });
    } catch (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (equipo) => {
    setModoEdicion(true);
    setEquipoEditado(equipo);
    setFormData({
      nombre: equipo.nombre || "",
      descripcion: equipo.descripcion || "",
      categoria: equipo.categoria || "",
      codigo: equipo.codigo || "",
      marca: equipo.marca || "",
      modelo: equipo.modelo || "",
      serie: equipo.serie || "",
      estado: equipo.estado || "Disponible",
      trabajadorDni: equipo.trabajadorDni || "",
      trabajadorNombre: equipo.trabajadorNombre || "",
      fechaPrestamo: equipo.fechaPrestamo || null,
    });
    setShowModal(true);
  };

  const handleEliminar = async (id) => {
    try {
      const result = await Swal.fire({
        title: "¿Estás seguro?",
        text: "Esta acción no se puede deshacer",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#f97316",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });
      if (result.isConfirmed) {
        await deleteDoc(doc(db, "equipos_catalogo", id));
        await cargarEquipos();
        Swal.fire("¡Eliminado!", "El equipo ha sido eliminado.", "success");
      }
    } catch (error) {
      Swal.fire({ title: "Error", text: "No se pudo eliminar el equipo", icon: "error" });
    }
  };

  const limpiarFormulario = () => {
    setFormData({
      nombre: "",
      descripcion: "",
      categoria: "",
      codigo: "",
      marca: "",
      modelo: "",
      serie: "",
      estado: "Disponible",
      trabajadorDni: "",
      trabajadorNombre: "",
      fechaPrestamo: null,
    });
    setModoEdicion(false);
    setEquipoEditado(null);
  };

  const handleDescargarPlantilla = () => {
    // 1. Hoja de Plantilla con Ejemplo
    const data = [
      ["codigo", "nombre", "categoria", "marca", "modelo", "serie", "estado", "descripcion"],
      ["E-001", "LAPTOP DELL LATITUDE", "computo", "DELL", "5420", "SN-987654", "Disponible", "Equipo para oficina de ingeniería"],
      ["E-002", "MULTÍMETRO FLUKE", "medicion", "FLUKE", "115", "SN-456123", "Disponible", "Multímetro digital de precisión"]
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "PlantillaEquipos");

    // 2. Hoja de Ayuda (Instrucciones)
    const helpData = [
      ["COLUMNA", "DESCRIPCIÓN", "OBLIGATORIO", "VALORES RECOMENDADOS / EJEMPLO"],
      ["codigo", "Código único del equipo", "SI", "E-001"],
      ["nombre", "Nombre del equipo", "SI", "RADIO COMUNICADOR"],
      ["categoria", "Categoría del sistema (Ver abajo)", "SI", "computo, medicion, comunicaciones, etc."],
      ["marca", "Marca del fabricante", "NO", "MOTOROLA"],
      ["modelo", "Modelo del equipo", "NO", "PRO-5150"],
      ["serie", "Número de serie físico", "NO", "ABC123456"],
      ["estado", "Estado inicial del equipo", "SI", "Disponible (siempre usar este por defecto)"],
      ["descripcion", "Detalles adicionales", "NO", "En maletín con cargador"],
      ["", "", "", ""],
      ["CATEGORÍAS DE EQUIPOS (Escribir exactamente así):", "", "", ""],
      [CATEGORIAS_EQUIPOS.join(", "), "", "", ""],
      ["", "", "", ""],
      ["NOTA:", "El estado por defecto debe ser 'Disponible' para que aparezca en el almacén.", "", ""]
    ];
    const wsHelp = XLSX.utils.aoa_to_sheet(helpData);
    XLSX.utils.book_append_sheet(wb, wsHelp, "AYUDA_INSTRUCCIONES");

    XLSX.writeFile(wb, "plantilla_equipos_con_guia.xlsx");
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet);

      const nuevosEquipos = rows.map((row) => ({
        codigo: row.codigo || row.Código || "",
        nombre: row.nombre || row.Nombre || "",
        categoria: (row.categoria || row.Categoría || "").toLowerCase(),
        marca: row.marca || row.Marca || "",
        modelo: row.modelo || row.Modelo || "",
        serie: row.serie || row.Serie || "",
        estado: row.estado || row.Estado || "Disponible",
        descripcion: row.descripcion || row.Descripción || "",
        fechaRegistro: new Date(),
        activo: true,
        ruc_empresa: ruc,
      }));

      const validos = nuevosEquipos.filter(
        eq =>
          eq.codigo &&
          eq.nombre &&
          eq.categoria &&
          CATEGORIAS_EQUIPOS.includes(eq.categoria)
      );

      for (const eq of validos) {
        await addDoc(collection(db, "equipos_catalogo"), eq);
      }

      await cargarEquipos();
      Swal.fire("¡Éxito!", `Se importaron ${validos.length} equipos correctamente.`, "success");
    } catch (error) {
      Swal.fire("Error", "No se pudo importar el archivo.", "error");
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  };

  const equiposFiltrados = equipos.filter((eq) => {
    const cumpleBusqueda =
      eq.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
      (eq.codigo || "").toString().includes(busqueda) ||
      (eq.serie || "").toString().toLowerCase().includes(busqueda.toLowerCase());
    const cumpleCategoria =
      !filtroCategoria || eq.categoria === filtroCategoria;
    return cumpleBusqueda && cumpleCategoria;
  });

  const getBadgeColor = (estado) => {
    switch (estado) {
      case "Disponible": return "success";
      case "Prestado": return "warning";
      case "Mantenimiento": return "info";
      case "Baja": return "danger";
      default: return "secondary";
    }
  };

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        padding: "20px 0",
      }}
    >
      <Container fluid className="px-lg-5 px-3">
        <Card className="shadow-lg border-0" style={{ borderRadius: "15px", overflow: "hidden" }}>
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
              borderBottom: "3px solid #ea580c",
            }}
          >
            <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
              <BotonManualHeader usuario={usuario} />
            </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaTools className="me-3" />
                Catálogo de Equipos
              </h2>
              <p className="mb-0 opacity-75">Control de Equipos y Activos por Empresa</p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Barra superior con acciones */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={1}>
                    <div
                      className="rounded-3 p-3 text-center"
                      style={{
                        background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
                        color: "white",
                      }}
                    >
                      <BiDevices size={24} />
                    </div>
                  </Col>
                  <Col md={7}>
                    <h5 className="mb-1 fw-semibold">Acciones del Catálogo</h5>
                    <p className="text-muted mb-0 small">
                      Gestione sus activos, equipos de medición y herramientas eléctricas
                    </p>
                  </Col>
                  <Col md={4} className="text-end d-flex gap-2 justify-content-end">
                    <Button
                      variant="light"
                      onClick={() => {
                        limpiarFormulario();
                        setShowModal(true);
                      }}
                      className="d-flex align-items-center gap-2"
                      style={{ borderRadius: "10px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}
                    >
                      <FaPlus size="16px" /> Nuevo Equipo
                    </Button>
                    <Form.Label
                      className="btn btn-light mb-0 d-flex align-items-center gap-2"
                      style={{ borderRadius: "10px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)", cursor: "pointer" }}
                    >
                      <FaFileImport size="16px" /> Importar
                      <Form.Control
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={handleImportExcel}
                        style={{ display: "none" }}
                      />
                    </Form.Label>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Fila de KPIs */}
            <Row className="mb-4 g-3">
              <Col md={3}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)", color: "white", borderRadius: "15px" }}>
                    <h4 className="fw-bold mb-1">{stats.total}</h4>
                    <p className="mb-0 opacity-90 small">Total Equipos</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #059669 0%, #10b981 100%)", color: "white", borderRadius: "15px" }}>
                    <h4 className="fw-bold mb-1">{stats.disponibles}</h4>
                    <p className="mb-0 opacity-90 small">Disponibles</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)", color: "white", borderRadius: "15px" }}>
                    <h4 className="fw-bold mb-1">{stats.prestados}</h4>
                    <p className="mb-0 opacity-90 small">En Préstamo</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)", color: "white", borderRadius: "15px" }}>
                    <h4 className="fw-bold mb-1">{stats.mantenimiento}</h4>
                    <p className="mb-0 opacity-90 small">Baja / Mantenimiento</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Búsqueda */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-3">
                <Row className="g-3">
                  <Col md={5}>
                    <div className="position-relative">
                      <FaSearch className="position-absolute" style={{ left: "15px", top: "50%", transform: "translateY(-50%)", color: "#6c757d" }} />
                      <Form.Control
                        type="text"
                        placeholder="Buscar por nombre, código o serie..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        className="ps-5 form-control-lg"
                        style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                      />
                    </div>
                  </Col>
                  <Col md={4}>
                    <Form.Select
                      value={filtroCategoria}
                      onChange={(e) => setFiltroCategoria(e.target.value)}
                      className="form-control-lg"
                      style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                    >
                      <option value="">Todas las categorías</option>
                      {CATEGORIAS_EQUIPOS.map(cat => (
                        <option key={cat} value={cat}>{cat.toUpperCase()}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={3} className="d-flex justify-content-end align-items-center">
                    <Button
                      variant="outline-warning"
                      onClick={handleDescargarPlantilla}
                      className="d-flex align-items-center gap-2 py-2 px-3 fw-bold w-100"
                      style={{ borderRadius: "10px", border: "2px solid #f97316" }}
                    >
                      <FaDownload /> Plantilla Excel
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Tabla */}
            <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px", overflow: "hidden" }}>
              <Card.Header
                className="text-white py-3"
                style={{
                  background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
                  border: "none"
                }}
              >
                <div className="d-flex align-items-center">
                  <FaDesktop className="me-2" />
                  <div className="flex-grow-1">
                    <h5 className="mb-0 fw-semibold">Inventario de Equipos</h5>
                    <small className="opacity-75">Control de activos y estados operativos</small>
                  </div>
                  <Badge bg="light" text="dark">
                    {equiposFiltrados.length} Registros
                  </Badge>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive" style={{ maxHeight: "600px" }}>
                  <Table hover className="align-middle mb-0" style={{ fontSize: "0.9rem" }}>
                    <thead style={{ backgroundColor: "#f8f9fa", position: "sticky", top: 0, zIndex: 10 }}>
                      <tr>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold">Código</th>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold">Equipo</th>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold">Categoría</th>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold">Marca/Modelo</th>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold text-center">Estado</th>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold">Responsable</th>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {equiposFiltrados.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-5">
                            <h5 className="text-muted">No se encontraron equipos</h5>
                          </td>
                        </tr>
                      ) : (
                        equiposFiltrados.map((eq) => (
                          <tr key={eq.id}>
                            <td className="py-3 px-3 fw-bold">{eq.codigo}</td>
                            <td className="py-3 px-3">
                              <div className="fw-bold">{eq.nombre}</div>
                              <small className="text-muted text-truncate d-block" style={{ maxWidth: "200px" }}>
                                {eq.descripcion}
                              </small>
                            </td>
                            <td className="py-3 px-3 text-capitalize">{eq.categoria}</td>
                            <td className="py-3 px-3">
                              {eq.marca} <br />
                              <small className="text-muted">{eq.modelo}</small>
                            </td>
                            <td className="py-3 px-3 text-center">
                              <Badge bg={getBadgeColor(eq.estado)} pill style={{ fontSize: "0.80rem" }}>
                                {eq.estado}
                              </Badge>
                            </td>
                            <td className="py-3 px-3">
                              {eq.estado === "Prestado" ? (
                                <div>
                                  <div className="fw-bold text-orange small">{eq.trabajadorNombre}</div>
                                  <small className="text-muted">{eq.trabajadorDni}</small>
                                </div>
                              ) : "-"}
                            </td>
                            <td className="py-3 px-3">
                              <div className="d-flex gap-1 justify-content-center">
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  onClick={() => handleEditar(eq)}
                                  style={{ borderRadius: "6px" }}
                                >
                                  <BiEdit size="14px" />
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleEliminar(eq.id)}
                                  style={{ borderRadius: "6px" }}
                                >
                                  <BiTrash size="14px" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Card.Body>
        </Card>
      </Container>

      {/* Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold d-flex align-items-center text-dark">
            <div
              className="rounded-circle p-2 me-3 d-flex align-items-center justify-content-center"
              style={{ background: "rgba(249, 115, 22, 0.1)", color: "#f97316" }}
            >
              <FaTools size={20} />
            </div>
            {modoEdicion ? "Editar Equipo" : "Registrar Nuevo Equipo"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form onSubmit={handleSubmit}>
            <Row className="g-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold">Código</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.codigo}
                    onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                    required
                    className="form-control-lg"
                  />
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group>
                  <Form.Label className="fw-bold">Nombre del Equipo</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    required
                    className="form-control-lg"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Categoría</Form.Label>
                  <Form.Select
                    value={formData.categoria}
                    onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                    required
                    className="form-control-lg"
                  >
                    <option value="">Seleccione...</option>
                    {CATEGORIAS_EQUIPOS.map(cat => <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Estado</Form.Label>
                  <Form.Select
                    value={formData.estado}
                    onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                    required
                    className="form-control-lg"
                  >
                    <option value="Disponible">Disponible</option>
                    <option value="Prestado">Prestado</option>
                    <option value="Mantenimiento">Mantenimiento</option>
                    <option value="Baja">Baja</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold">Marca</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.marca}
                    onChange={(e) => setFormData({ ...formData, marca: e.target.value })}
                    className="form-control-lg"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold">Modelo</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.modelo}
                    onChange={(e) => setFormData({ ...formData, modelo: e.target.value })}
                    className="form-control-lg"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold">N° de Serie</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.serie}
                    onChange={(e) => setFormData({ ...formData, serie: e.target.value })}
                    className="form-control-lg"
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-bold">Descripción / Observaciones</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.descripcion}
                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                    className="form-control-lg"
                  />
                </Form.Group>
              </Col>
              {formData.estado === "Prestado" && (
                <Col md={12}>
                  <div className="alert alert-warning">
                    <strong>Aviso:</strong> Si el equipo está prestado, se recomienda gestionarlo desde el módulo de Préstamos para mantener el historial.
                  </div>
                </Col>
              )}
            </Row>
            <div className="d-flex gap-2 justify-content-end mt-4">
              <Button variant="light" size="lg" onClick={() => setShowModal(false)} className="px-4">Cancelar</Button>
              <Button
                variant="primary"
                type="submit"
                disabled={loading}
                size="lg"
                className="px-4 text-white fw-bold"
                style={{ background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)", border: "none", borderRadius: "10px" }}
              >
                {loading ? "Guardando..." : (modoEdicion ? "Actualizar Equipo" : "Guardar Equipo")}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default CatalogoEquipos;